/**
 * WorkerOrganization — worker portal 'My Organization' page (P0.6).
 *
 * Single round-trip backed by GET /v1/me/organization. Renders the
 * worker's organizational context as 4-5 stacked cards in §13 chrome:
 *
 *   1. Org card     — company name + logo + industry + size +
 *                     timezone + website (Apple-style banner)
 *   2. Department   — name + parent (breadcrumb) + member_count
 *                     + 'You manage this department' note when
 *                     applicable
 *   3. Manager      — direct manager card (worker.manager_id chain
 *                     beats dept.manager_id; small caption shows
 *                     which one was used)
 *   4. Peers        — same-department workers as a wrap of compact
 *                     cards. Self pinned first with a 'You' chip,
 *                     dept manager marked with a 'Manager' chip
 *   5. Branch + Loc — side-by-side cards (single row on desktop,
 *                     stacks on mobile)
 *
 * Empty states are inline per block (caption with 'No X assigned —
 * contact HR') rather than hiding the card, so the page layout
 * stays predictable across worker shapes.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar, Box, Chip, CircularProgress, Paper, Stack,
  Typography,
} from '@mui/material';
import {
  AccountTreeOutlined,
  BusinessOutlined,
  EmailOutlined,
  GroupOutlined,
  LanguageOutlined,
  PhoneOutlined,
  PlaceOutlined,
  PublicOutlined,
  StoreOutlined,
  SupervisorAccountOutlined,
} from '@mui/icons-material';
import { meApi } from '../../api';
import type { OrganizationResponse, OrgPeer } from '../../api/me';
import { useTranslation } from 'react-i18next';

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Card chrome shared by every block. Mirrors svdy-ui.md §1: 1px
 * border, no shadow, borderRadius 2, no hover lift. Padding tuned
 * for 13" laptop density.
 *
 * `fullHeight` makes the card stretch to fill its Grid item so
 * adjacent cards in the same row align at top AND bottom — fixes
 * the jagged 'taller column wins, shorter card has white space
 * below' look that read as 草台 (Addendum 24 polish round).
 */
const Card: React.FC<{ children: React.ReactNode; sx?: object; fullHeight?: boolean }> = ({
  children, sx, fullHeight,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      ...(fullHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
      ...sx,
    }}
  >
    {children}
  </Paper>
);

/**
 * Section header used inside each card (icon + title pair).
 *
 * `minHeight` gives the title row a consistent vertical footprint
 * regardless of whether `sub` is rendered, so two side-by-side
 * cards (e.g. Department + Manager) always align their first
 * content row.
 */
const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; sub?: string }> = ({
  icon, title, sub,
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75, minHeight: 28 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', minHeight: 14, lineHeight: 1.2 }}
      >
        {sub ?? '\u00A0'}
      </Typography>
    </Box>
  </Stack>
);

/**
 * Small empty caption shown inside a card when its block is null.
 * Keeps the card visible so the page layout doesn't shift.
 */
const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.disabled">
    {children}
  </Typography>
);

const PeerCard: React.FC<{ peer: OrgPeer }> = ({ peer }) => {
  const { t } = useTranslation();
  return (
  <Paper
    elevation={0}
    sx={{
      px: 1.5, py: 1,
      border: '1px solid',
      borderColor: peer.is_self ? 'primary.main' : 'divider',
      borderRadius: 1.5,
      bgcolor: peer.is_self ? 'action.hover' : 'background.paper',
      // Width is driven by the parent CSS Grid (auto-fill / minmax)
      // so peer cards stretch evenly across the row. Pre-fix this
      // was a fixed 220px which left a ragged right edge whenever
      // the available width didn't divide cleanly — the visual
      // 'didn't align' / 草台 complaint from Addendum 24.
      width: '100%',
      display: 'flex',
      gap: 1,
      alignItems: 'center',
    }}
  >
    <Avatar
      src={peer.avatar_url ?? undefined}
      sx={{ width: 32, height: 32, fontSize: '0.7rem', bgcolor: peer.is_manager ? 'secondary.main' : 'primary.main' }}
    >
      {initials(peer.name)}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.825rem' }}
          noWrap
          title={peer.name}
        >
          {peer.name}
        </Typography>
        {peer.is_self && (
          <Chip
            label={t('worker:organization.peerYou')}
            size="small"
            color="primary"
            sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
          />
        )}
        {peer.is_manager && (
          <Chip
            label={t('worker:organization.peerManager')}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
          />
        )}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem' }}
        noWrap
        title={peer.position ?? ''}
      >
        {peer.position ?? '—'}
      </Typography>
    </Box>
  </Paper>
  );
};

const WorkerOrganization: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<OrganizationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await meApi.organization.get();
      setData(body);
    } catch (e) {
      setError((e as Error).message ?? t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        py: '12px', width: '100%',
      }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:organization.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {error ?? t('worker:organization.noData')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const { self: me, department, manager, peers, branch, location, org } = data;
  const youManageThisDept =
    department?.manager_worker_id != null &&
    department.manager_worker_id === me.worker_id;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: '12px',
      width: '100%',
    }}>
      {/* Page header */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:organization.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('worker:organization.orgSubtitle')}
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ width: '100%' }}>
        {/* Org banner card */}
        <Card>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {org.logo_url ? (
              <Avatar
                src={org.logo_url}
                variant="rounded"
                sx={{ width: 56, height: 56 }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.1rem' }}
              >
                {initials(org.name)}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }} noWrap>
                {org.name}
              </Typography>
              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                {org.industry && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <BusinessOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {org.industry}
                    </Typography>
                  </Box>
                )}
                {org.company_size && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <GroupOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {t('worker:organization.employees', { size: org.company_size })}
                    </Typography>
                  </Box>
                )}
                {org.timezone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <PublicOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {org.timezone}
                    </Typography>
                  </Box>
                )}
                {org.website && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0, maxWidth: 240 }}>
                    <LanguageOutlined sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      component="a"
                      href={org.website}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={org.website}
                    >
                      {org.website.replace(/^https?:\/\//, '')}
                    </Typography>
                  </Box>
                )}
              </Stack>
              {org.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {org.description}
                </Typography>
              )}
            </Box>
          </Stack>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          {/* Department — direct grid cell, no MUI Grid wrapper.
              MUI Grid container's negative-margin trick was making
              the inner cards drift left/right of the page-wide
              Card edges (Org banner / Peers); CSS Grid has no
              such shenanigans, so left/right edges line up
              exactly with the full-width cards above and below. */}
          <Card fullHeight>
            <SectionHeader
              icon={<AccountTreeOutlined fontSize="small" />}
              title={t('worker:organization.department')}
            />
            {department ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  {department.parent_name ? (
                    <>
                      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {department.parent_name} ›{' '}
                      </Box>
                      {department.name}
                    </>
                  ) : (
                    department.name
                  )}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('worker:organization.activeMembers', { count: department.member_count })}
                  </Typography>
                  {department.code && (
                    <Typography variant="caption" color="text.secondary">
                      {t('worker:organization.codeLabel', { code: department.code })}
                    </Typography>
                  )}
                </Stack>
                {youManageThisDept && (
                  <Chip
                    label={t('worker:organization.youManage')}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {department.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {department.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <EmptyHint>
                {t('worker:organization.noDepartment')}
              </EmptyHint>
            )}
          </Card>

          {/* Manager */}
          <Card fullHeight>
            <SectionHeader
              icon={<SupervisorAccountOutlined fontSize="small" />}
              title={t('worker:organization.manager')}
            />
            {manager ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  src={manager.avatar_url ?? undefined}
                  sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.8rem' }}
                >
                  {initials(manager.name)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {manager.name}
                    </Typography>
                    {manager.source === 'department.manager_id' && (
                      <Chip
                        label={t('worker:organization.managerSourceDept')}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem', borderRadius: 1, flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                  {manager.position && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {manager.position}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {manager.email && (
                      <Box
                        component="a"
                        href={`mailto:${manager.email}`}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          color: 'primary.main', textDecoration: 'none',
                        }}
                      >
                        <EmailOutlined sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{manager.email}</Typography>
                      </Box>
                    )}
                    {manager.phone && (
                      <Box
                        component="a"
                        href={`tel:${manager.phone}`}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          color: 'text.secondary', textDecoration: 'none',
                        }}
                      >
                        <PhoneOutlined sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{manager.phone}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            ) : youManageThisDept ? (
              <EmptyHint>
                {t('worker:organization.noManagerYouManage')}
              </EmptyHint>
            ) : (
              <EmptyHint>
                {t('worker:organization.noManager')}
              </EmptyHint>
            )}
          </Card>
        </Box>

        {/* Peers */}
        <Card>
          <SectionHeader
            icon={<GroupOutlined fontSize="small" />}
            title={t('worker:organization.deptMembers')}
            sub={
              department
                ? t('worker:organization.peersIn', { count: peers.length, dept: department.name })
                : t('worker:organization.peersListed', { count: peers.length })
            }
          />
          {peers.length === 0 ? (
            <EmptyHint>{t('worker:organization.noTeamMembers')}</EmptyHint>
          ) : (
            // CSS Grid auto-fill stretches each card to fill the
            // row evenly. Min 220px keeps cards readable; the
            // remaining space is split equally between columns
            // so the right edge always aligns. Fixes the ragged
            // flex-wrap layout the user flagged as 草台.
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(180px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(220px, 1fr))',
                },
                gap: 1,
              }}
            >
              {/* Pin self first, then manager, then alpha by name. */}
              {[...peers]
                .sort((a, b) => {
                  if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
                  if (a.is_manager !== b.is_manager) return a.is_manager ? -1 : 1;
                  return a.name.localeCompare(b.name);
                })
                .map((p) => (
                  <PeerCard key={p.worker_id} peer={p} />
                ))}
            </Box>
          )}
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          {/* Branch */}
          <Card fullHeight>
            <SectionHeader
              icon={<StoreOutlined fontSize="small" />}
              title={t('worker:organization.branch')}
              sub={branch?.is_primary ? t('worker:organization.primarySite') : undefined}
            />
            {branch ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {branch.name}
                </Typography>
                {branch.code && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('worker:organization.codeLabel', { code: branch.code })}
                  </Typography>
                )}
                {branch.address && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {branch.address}
                  </Typography>
                )}
              </Box>
            ) : (
              <EmptyHint>{t('worker:organization.noBranch')}</EmptyHint>
            )}
          </Card>

          {/* Location */}
          <Card fullHeight>
            <SectionHeader
              icon={<PlaceOutlined fontSize="small" />}
              title={t('worker:organization.workLocation')}
            />
            {location ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {location.name}
                </Typography>
                {(location.street_address || location.address) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {location.street_address ?? location.address}
                  </Typography>
                )}
                {(location.city || location.state || location.country) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {[location.city, location.state, location.country]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
                {location.timezone && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t('worker:organization.timezoneLabel', { tz: location.timezone })}
                  </Typography>
                )}
                {(location.primary_contact_name || location.phone) && (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                    {location.primary_contact_name && (
                      <Typography variant="caption" color="text.secondary">
                        {t('worker:organization.contactLabel')} <strong>{location.primary_contact_name}</strong>
                      </Typography>
                    )}
                    {location.phone && (
                      <Box
                        component="a"
                        href={`tel:${location.phone}`}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          color: 'text.secondary', textDecoration: 'none',
                        }}
                      >
                        <PhoneOutlined sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{location.phone}</Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            ) : (
              <EmptyHint>{t('worker:organization.noLocation')}</EmptyHint>
            )}
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default WorkerOrganization;
